/* ============================================================================
   DRAWERS — the shared shell (scrim + panel, focus, scroll lock) and the two
   drawers built on it: the size drawer and Quick Buy.

   Choreography is the 06 cart-drawer decision, ported to motion.dev: the
   panel arrives first on --mo-slow / entrance; the Glass Overlay settles in
   behind it on --mo-base, 120ms later. Leaving is decisive: --mo-base on the
   exit curve. Desktop panels come from the right (600px, Figma "D Quickbuy");
   mobile panels are full-height sheets rising from the bottom, where the CTA
   that opened them sits. Reduced: no travel, a crossfade.
   ========================================================================== */

import { animate, stagger, MO, state, play, track, SPRING_RELEASE, $, $$, mqDesktop } from './mo.js';
import { SIZE_NAMES, moneyShort } from './data.js';
import { figsSavedSize } from '/src/figs-nav/figs-account.js';
import {
  renderColors, checkSwatch, renderSizes, renderTypes, checkTile, markSoldOut, bindRoving,
  swapText, swapButtonLabel, pulse, shake, announce, tm,
} from './ui.js';

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
const KEEP_LIVE = 'script, [data-lab-bar], [data-lab-handle], .lab-grid, [data-live]';

/* `mode: 'takeover'` is the full-screen variant (Visual Color Compare, the
   gallery overlay): no travel — it settles in from 98.5% on --mo-slow and
   leaves on the exit curve, with the scrim (if it has one) on the drawer's
   timing. Focus, inert, Escape and the Tab loop are the same either way.
   `open(trigger, { onDismiss })` runs onDismiss when the user closes the
   drawer (close button, scrim, Escape) — never on a programmatic close() —
   which is how a drawer opened from another drawer hands back to it.
   `side: true` keeps a drawer arriving from the right on phones too, rising
   nowhere (the account pages' edit drawers, like the nav's own); it also
   opens at the top of its content every time. */
export function createDrawer(root, { mode = 'drawer', side = false } = {}) {
  const takeover = mode === 'takeover';
  const scrim = root.querySelector('[data-drawer-scrim]');
  const panel = root.querySelector('[data-drawer-panel]');
  let isOpen = false;
  let opener = null;
  let inerted = [];
  let closing = null;
  let axis = 'x';
  let onDismiss = null;

  function lockPage(on) {
    document.documentElement.style.overflow = on ? 'hidden' : '';
    document.body.style.overflow = on ? 'hidden' : '';
    if (on) {
      inerted = [...document.body.children].filter((el) => el !== root && !el.inert && !el.matches(KEEP_LIVE));
      inerted.forEach((el) => { el.inert = true; });
    } else {
      inerted.forEach((el) => { el.inert = false; });
      inerted = [];
    }
  }

  function open(trigger, opts = {}) {
    if (isOpen) return;
    isOpen = true;
    opener = trigger || document.activeElement;
    onDismiss = opts.onDismiss || null;
    axis = side || mqDesktop.matches ? 'x' : 'y';
    if (side) panel.scrollTop = 0;
    const other = axis === 'x' ? 'y' : 'x';
    root.hidden = false;
    lockPage(true);
    if (takeover) {
      track(animate(panel, state.reduced ? { opacity: [0, 1] } : { opacity: [0, 1], scale: [0.985, 1] },
        { duration: state.reduced ? MO.base : MO.slow, ease: MO.eEntrance }));
      if (scrim) track(animate(scrim, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard }));
    } else if (state.reduced) {
      /* no travel: park the panel in place first, then only fade */
      animate(panel, { [axis]: '0%', [other]: '0%' }, { duration: 0 });
      track(animate(panel, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard }));
      track(animate(scrim, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard }));
    } else {
      track(animate(panel, { [axis]: ['100%', '0%'], [other]: '0%', opacity: 1 }, { duration: MO.slow, ease: MO.eEntrance }));
      track(animate(scrim, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard, delay: 0.12 }));
    }
    /* the panel takes focus at once (the page behind is now inert); the
       first control takes it once the panel has arrived, not mid-travel */
    panel.focus({ preventScroll: true });
    setTimeout(() => {
      if (!isOpen) return;
      panel.querySelector('[data-autofocus]')?.focus({ preventScroll: true });
    }, state.reduced ? 0 : 320);
    root.dispatchEvent(new CustomEvent('drawer:open'));
  }

  function close() {
    if (!isOpen) return closing || Promise.resolve();
    isOpen = false;
    onDismiss = null;
    const anims = takeover
      ? [
        track(animate(panel, state.reduced ? { opacity: [1, 0] } : { opacity: [1, 0], scale: [1, 0.985] },
          { duration: MO.base, ease: MO.eExit })),
        scrim && track(animate(scrim, { opacity: [1, 0] }, { duration: MO.base, ease: MO.eStandard })),
      ]
      : state.reduced
      ? [
        track(animate(panel, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eStandard })),
        track(animate(scrim, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eStandard })),
      ]
      : [
        track(animate(panel, { [axis]: ['0%', '100%'] }, { duration: MO.base, ease: MO.eExit })),
        track(animate(scrim, { opacity: [1, 0] }, { duration: MO.base, ease: MO.eStandard })),
      ];
    lockPage(false);
    opener?.focus?.({ preventScroll: true });
    root.dispatchEvent(new CustomEvent('drawer:close'));
    closing = Promise.all(anims.map((a) => a?.finished)).catch(() => {}).then(() => {
      if (!isOpen) root.hidden = true;
      closing = null;
    });
    return closing;
  }

  function dismiss() {
    const back = onDismiss;
    close();
    back?.();
  }

  scrim?.addEventListener('click', dismiss);
  root.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', dismiss));
  document.addEventListener('keydown', (e) => {
    /* defaultPrevented: one Escape closes one drawer, even when dismissing
       it re-opens the drawer underneath during this same event */
    if (isOpen && e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); dismiss(); }
  });
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusables = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  return { open, close, root, panel, get isOpen() { return isOpen; } };
}

/* ============================================================================
   SIZE DRAWER — "Select a Size" opens it with nothing preselected: the
   shopper picks a size and a type (Regular / Petite), in either order. Each
   pick marks its tile and label ("X-Small—Size", "Regular—Type"; the size
   also goes on the title, "—XS"); once both are set, the drawer closes itself
   after a beat long enough to read the confirmation.
   Opened again to change them (CHANGE SIZE), it holds the current pair and
   shows Figma's Selected footer: picks wait for SELECT SIZE, and closing
   without it leaves the page as it was.
   The pants' second variant is Length (Regular / Petite / Tall) where the
   tops' is Type — product.typeLabel names it. A size sold out in the page's
   colour (Size Drawer/Soldout 1126:86493) is struck through, and so is every
   length once it's the size marked; both stay choosable, since choosing them
   is the way to the waitlist.
   `root` lets a page run a second drawer from the same markup (the
   cross-sell's), `preselectType` starts it on the first type so one size tap
   completes it, and setProduct() re-fills it for another product.
   ========================================================================== */
const AUTO_CLOSE_MS = 450;
/* the second variant's heading: tops "Size Type", pants "Length", shoes "Width" */
const typeHeading = (p) => (!p.typeLabel || p.typeLabel === 'Type' ? 'Size Type' : p.typeLabel);

export function initSizeDrawer({
  product, onPick, onModels, onSizeChart, root = $('[data-drawer="size"]'),
  getColor = () => null, isSoldOut = () => false, preselectType = false,
}) {
  const drawer = createDrawer(root);
  const grid = $('[data-size-grid]', root);
  const types = $('[data-type-grid]', root);
  const typeGroup = types.closest('.size-drawer__group');
  const titleName = $('[data-size-title-name]', root);
  const suffix = $('[data-size-title-suffix]', root);
  const label = $('[data-size-drawer-label]', root);
  const typeLabel = $('[data-type-drawer-label]', root);
  const fitTip = $('[data-size-fit]', root); // absent in the cross-sell's drawer
  if (fitTip) fitTip.textContent = product.fitTip;
  /* the fit tip is the model selector's way in (Figma's only "model" copy);
     SIZE CHART swaps this panel for the chart the same way. Dismissing
     either comes back to this drawer, focused on the link that opened it —
     the page button that opened this drawer stays the final way out. */
  let pageTrigger = null;
  let dismissed = null;
  const handOff = (fn) => (e) => {
    const link = e.currentTarget;
    clearTimeout(closeTimer);
    drawer.close();
    fn?.(link, () => api.open(pageTrigger, { focus: link }), { ...draft });
  };
  $('[data-open-models]', root)?.addEventListener('click', handOff(onModels));
  $$('[data-open-size-chart]', root).forEach((b) => b.addEventListener('click', handOff(onSizeChart)));

  /* `committed` is what the page shows; `draft` is what's marked here. A
     product with a single type has nothing to choose, so it's set already. */
  const firstType = () => (product.types.length === 1 || preselectType ? product.types[0] : null);
  const committed = { size: null, type: firstType() };
  const draft = { ...committed };
  let changing = false; // Selected footer up: picks wait for SELECT SIZE
  let closeTimer = 0;
  const word = () => product.typeLabel || 'Type';
  /* plain labels, no superscript (Size Variants 804:131181): "Size: X-Small",
     "Size Type: Regular" — "Length: Regular" on the pants */
  const sizeLabelHTML = (s) => (s ? `Size: ${SIZE_NAMES[s] || s}` : 'Select Size');
  const typeLabelHTML = (t) => (t ? `${typeHeading(product)}: ${t}` : `Select ${typeHeading(product)}`);
  /* sold out in the page's colour: the size, and every length of it */
  const syncSoldOut = () => {
    const color = getColor();
    markSoldOut(grid, (t) => isSoldOut(color, t.dataset.size));
    markSoldOut(types, () => Boolean(draft.size) && isSoldOut(color, draft.size));
  };

  const commit = () => {
    const changed = draft.size !== committed.size || draft.type !== committed.type;
    Object.assign(committed, draft);
    if (changed) onPick(committed.size, committed.type);
  };
  /* first time through, completing the pair is the commit */
  const autoClose = () => {
    clearTimeout(closeTimer);
    if (changing || !draft.size || !draft.type) return;
    closeTimer = setTimeout(() => { drawer.close(); commit(); }, state.reduced ? 200 : AUTO_CLOSE_MS);
  };

  /* Size Drawer/Selected (877:186198 · M 877:186752): SELECT SIZE commits
     whatever is marked — the pair as it was, or a new one — and closes */
  const foot = $('[data-size-foot]', root);
  const confirmBtn = $('[data-size-confirm]', root);
  confirmBtn.addEventListener('click', () => {
    clearTimeout(closeTimer);
    announce(`${SIZE_NAMES[draft.size] || draft.size} ${draft.type} selected`);
    drawer.close();
    commit();
  });

  function fill() {
    renderSizes(grid, product.sizes, null);
    renderTypes(types, product.types, draft.type);
    typeGroup.hidden = product.types.length < 2;
    if (titleName) titleName.innerHTML = tm(product.name);
    if (fitTip) fitTip.textContent = product.fitTip;
  }
  fill();

  /* arrows move focus without choosing (Enter / Space choose) in both
     groups: either pick can be the one that closes the drawer */
  bindRoving(grid, (tile) => {
    const picked = tile.dataset.size;
    draft.size = picked;
    checkTile(grid, 'size', picked);
    play(tile, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    swapText(suffix, `—${picked}`);
    swapText(label, sizeLabelHTML(picked));
    syncSoldOut();
    const out = isSoldOut(getColor(), picked) ? ', sold out' : '';
    announce(`${SIZE_NAMES[picked] || picked} selected${out}${draft.type ? '' : `. Select a ${word().toLowerCase()}.`}`);
    if (!draft.type) pulse(typeLabel.parentElement); // points at what's still missing
    autoClose();
  }, { selectOnArrow: false });

  bindRoving(types, (tile) => {
    const picked = tile.dataset.type;
    draft.type = picked;
    checkTile(types, 'type', picked);
    play(tile, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    swapText(typeLabel, typeLabelHTML(picked));
    announce(`${picked} ${word().toLowerCase()} selected${draft.size ? '' : '. Select a size.'}`);
    if (!draft.size) pulse(grid.parentElement);
    autoClose();
  }, { selectOnArrow: false });

  root.addEventListener('drawer:close', () => clearTimeout(closeTimer));

  const api = {
    /* `focus` is the hand-back from the size chart / model selector: land on
       that link, keep the original page trigger and what's marked, and skip
       the grid's assembly — the shopper has just seen it */
    open(trigger, { focus, onDismiss } = {}) {
      const handBack = Boolean(focus);
      if (!handBack) {
        pageTrigger = trigger || document.activeElement;
        dismissed = onDismiss || null; // kept across a hand-back from the chart / models
        Object.assign(draft, committed); // a fresh open marks what the page has
        changing = Boolean(committed.size);
      } else if (draft.size && draft.type) {
        changing = true; // finished the pair just before the detour: confirm it, don't spring a close
      }
      checkTile(grid, 'size', draft.size);
      if (!draft.size) grid.querySelector('[role="radio"]').tabIndex = 0;
      checkTile(types, 'type', draft.type);
      if (!draft.type) types.querySelector('[role="radio"]').tabIndex = 0;
      syncSoldOut();
      suffix.textContent = draft.size ? `—${draft.size}` : '';
      label.innerHTML = sizeLabelHTML(draft.size);
      typeLabel.innerHTML = typeLabelHTML(draft.type);
      root.querySelectorAll('[data-autofocus]').forEach((el) => el.removeAttribute('data-autofocus'));
      (focus || grid.querySelector('[aria-checked="true"]') || grid.querySelector('[role="radio"]')).setAttribute('data-autofocus', '');
      foot.toggleAttribute('data-confirm', changing);
      confirmBtn.hidden = !changing;
      drawer.open(pageTrigger, { onDismiss: dismissed });
      if (!state.reduced && !handBack) {
        /* the grid assembles behind the arriving panel — tight stagger so it
           reads as one surface, finishing inside the panel's own travel */
        play([...grid.children], { opacity: [0, 1], y: [MO.riseXs, 0] },
          { duration: MO.base, ease: MO.eEntrance, delay: stagger(0.012, { startDelay: 0.16 }) });
        /* and the confirmation rises in with it, so it reads as the way out */
        if (changing) play(confirmBtn, { opacity: [0, 1], y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: 0.16 });
      }
    },
    /* another product in the same drawer (the cross-sell's tops): the size
       carries over when the new product has it, the type starts fresh */
    setProduct(next) {
      product = next;
      const keep = committed.size && next.sizes.includes(committed.size) ? committed.size : null;
      Object.assign(committed, { size: keep, type: firstType() });
      Object.assign(draft, committed);
      fill();
    },
    /* a size set from outside, not picked here (the cross-sell following the
       pant's size, a signed-in shopper's saved size) — no onPick */
    preset(size, type) {
      if (!product.sizes.includes(size)) return;
      const t = type && product.types.includes(type) ? type : committed.type || firstType();
      Object.assign(committed, { size, type: t });
      Object.assign(draft, committed);
    },
    get value() { return { ...committed }; },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
  return api;
}

/* ============================================================================
   QUICK BUY — the Figma component (1100:87560), filled from a Shop the Look
   product. Size is required: ADD TO BAG without one shakes the grid and
   shows the form's error copy instead of failing silently. A successful add
   confirms on the button (spring, licensed here), bumps the bag, and the
   drawer closes itself; `onAdded` runs once it has gone (the PDP opens the
   bag there, so the two panels never stack).
   The Uniform Builder (15) runs the same drawer as "add to uniform": its
   `ctaText` / `addedText` name the action, a product without `colors` keeps
   the uniform's one colour (no swatch rows), `open(p, trigger, preset)` marks
   a size already in the outfit, `onFill` lets the page dress the drawer
   (its embroidery card) and `refresh()` re-reads the CTA when that changes
   the price. Left out, everything is the PDP's.
   ========================================================================== */
export function initQuickBuy({
  onAdd, onAdded = null, root = $('[data-drawer="quickbuy"]'), onFill = null,
  ctaText = (p) => `Add to Bag—${moneyShort(p.price)}`, addedText = 'Added to Bag',
}) {
  const drawer = createDrawer(root);
  const scroller = $('[data-qb]', root);
  const gallery = $('[data-qb-gallery]', root);
  const title = $('[data-qb-title]', root);
  const colors = $('[data-qb-colors]', root);
  const sizeGrid = $('[data-qb-size-grid]', root);
  const sizeLabel = $('[data-qb-size-label]', root);
  const error = $('[data-qb-error]', root);
  const typeGroup = $('[data-qb-type-group]', root);
  const typeGrid = $('[data-qb-type-grid]', root);
  const typeLabel = $('[data-qb-type-label]', root);
  const view = $('[data-qb-view]', root);
  const atb = $('[data-qb-atb]', root);
  const atbLabel = $('[data-qb-atb-label]', root);

  let product = null;
  const sel = { color: null, size: null, type: null };
  let busy = false;
  let closeTimer = 0;

  const atbText = () => ctaText(product, sel);
  const nameOf = (slug) => [...product.colors.core, ...product.colors.limited].find((c) => c.slug === slug)?.name;

  bindRoving(colors, (sw) => {
    if (sw.dataset.color === sel.color) return;
    sel.color = sw.dataset.color;
    checkSwatch(colors, sel.color);
    swapText($('[data-color-name]', colors), nameOf(sel.color));
  });

  bindRoving(sizeGrid, (tile) => {
    sel.size = tile.dataset.size;
    checkTile(sizeGrid, 'size', sel.size);
    play(tile, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    swapText(sizeLabel, `Size: ${SIZE_NAMES[sel.size] || sel.size}`);
    if (!busy) atbLabel.textContent = atbText();
    if (!error.hidden) {
      play(error, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => { error.hidden = true; });
      sizeGrid.removeAttribute('data-invalid');
    }
  }, { selectOnArrow: false });

  bindRoving(typeGrid, (tile) => {
    if (tile.dataset.type === sel.type) return;
    sel.type = tile.dataset.type;
    checkTile(typeGrid, 'type', sel.type);
    swapText(typeLabel, `${typeHeading(product)}: ${sel.type}`);
  });

  atb.addEventListener('click', async () => {
    if (busy || !product) return;
    if (!sel.size) {
      error.hidden = false;
      sizeGrid.setAttribute('data-invalid', '');
      announce('Please select a size.');
      /* the size grid can sit below the pinned actions — bring it into view
         first, so the error and the shake happen where the eye is */
      sizeGrid.closest('.size-drawer__group').scrollIntoView({ block: 'center', behavior: state.reduced ? 'auto' : 'smooth' });
      setTimeout(() => {
        play(error, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.base, ease: MO.eEntrance });
        shake(sizeGrid);
        sizeGrid.querySelector('[role="radio"]')?.focus({ preventScroll: true });
      }, state.reduced ? 0 : 280);
      return;
    }
    busy = true;
    onAdd(product, { ...sel }, atb);
    pulse(atb);
    await swapButtonLabel(atbLabel, addedText, { affirm: true });
    closeTimer = setTimeout(() => {
      drawer.close().then(() => { busy = false; atbLabel.textContent = atbText(); onAdded?.(); });
    }, state.reduced ? 600 : 1100);
  });

  root.addEventListener('drawer:close', () => clearTimeout(closeTimer));

  function fill(p, preset = {}) {
    product = p;
    /* a signed-in shopper's saved size (and type / length) is preselected
       when the page hasn't one of its own to restore (My Account → Preferences) */
    const saved = preset.size ? null : figsSavedSize(p);
    const want = preset.size || saved?.size;
    const size = want && p.sizes.includes(want) ? want : null;
    const wantType = preset.type || saved?.type;
    const type = wantType && p.types.includes(wantType) ? wantType : p.types[0];
    Object.assign(sel, { color: p.color, size, type });
    gallery.innerHTML = p.gallery.map((src, i) => `
      <figure class="qb__slide${p.galleryOnWhite ? ' qb__slide--white' : ''}${p.slideClass ? ` ${p.slideClass}` : ''}">
        <img src="${src}" alt="${p.name}, image ${i + 1} of ${p.gallery.length}" ${i > 1 ? 'loading="lazy"' : ''} decoding="async">
      </figure>`).join('');
    gallery.scrollLeft = 0;
    title.innerHTML = tm(p.name);
    colors.hidden = !p.colors;
    if (p.colors) renderColors(colors, p.colors, p.color);
    sizeLabel.innerHTML = size ? `Size: ${SIZE_NAMES[size] || size}` : 'Select Size';
    renderSizes(sizeGrid, p.sizes, size);
    sizeGrid.removeAttribute('data-invalid');
    error.hidden = true;
    typeGroup.hidden = p.types.length < 2;
    renderTypes(typeGrid, p.types, sel.type);
    typeLabel.innerHTML = `${typeHeading(p)}: ${sel.type}`;
    view.href = p.url;
    /* a lab prototype opens here; a product the lab hasn't built opens live, in a new tab */
    const external = /^https?:/.test(p.url) && !p.url.startsWith(location.origin);
    view.target = external ? '_blank' : '';
    view.rel = external ? 'noopener' : '';
    onFill?.(p, { ...sel });
    atbLabel.textContent = atbText();
    scroller.scrollTop = 0;
  }

  return {
    open(p, trigger, preset) {
      if (drawer.isOpen) return;
      busy = false;
      fill(p, preset);
      drawer.open(trigger);
      if (!state.reduced) {
        play([...gallery.children], { opacity: [0, 1], x: [24, 0] },
          { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.12 }) });
      }
    },
    close: () => drawer.close(),
    /* the page changed something the CTA reads (the builder's embroidery) */
    refresh() { if (product && !busy) swapText(atbLabel, atbText()); },
    get isOpen() { return drawer.isOpen; },
  };
}
